import {inject, Injectable} from '@angular/core';
import {AuthService} from '../../authentication/auth-service';
import {ClaimManagerBackend} from '../../environments/application-configurations';
import {environment} from '../../environments/environment';
import {ClaimListResponse} from '../entity/claim-list-response';
import {PresignedClaimFileUrlResponse} from '../entity/claim-file';
import {BatchDownloadResponse} from '../entity/claim-download';

@Injectable({
  providedIn: 'root'
})
export class ClaimListService {
  private readonly authService: AuthService = inject(AuthService)
  private readonly backendConfig: ClaimManagerBackend = environment.claimManagerBackend

  public async getAllClaims(
    year: number,
    pageSize: number = 5000,
    lastEvaluatedKey: string | undefined = undefined,
    startDate: Date | undefined = undefined,
    endDate: Date | undefined = undefined,
  ): Promise<ClaimListResponse> {
    const path = this.backendConfig.getAllClaimsPath

    const parameters: Record<string, string> = {
      year: year.toString(),
      page_size: pageSize.toString()
    };
    if (startDate && startDate.constructor.name === 'Date') {
      parameters['startDate'] = this.createDatePrefix(startDate)
    }
    if (endDate && endDate.constructor.name === 'Date') {
      parameters['endDate'] = this.createDatePrefix(endDate)
    }
    if (lastEvaluatedKey && lastEvaluatedKey.length > 0) {
      parameters['last_evaluated_key'] = lastEvaluatedKey
    }


    const queryParams = new URLSearchParams(parameters);
    const postFix = Object.keys(parameters).length > 0 ? `?${queryParams.toString()}` : ''

    const url = `${this.backendConfig.baseUrl}${path}${postFix}`

    const headers = new Headers();
    headers.set('Authorization', `Bearer ${this.authService.getIdToken()}`);

    return await fetch(
      url,
      {
        method: 'GET',
        headers: headers
      }
    ).then(r => r.json()) as ClaimListResponse
  }

  private createDatePrefix(date: Date): string {
    let s = date.toISOString()
      .replace('T', '')
      .replace('-', '')
      .replace(':', '');
    return s.substring(0, Math.min(s.length, 8));

  }

  public async getClaimReportDownloadUrls(claimId: string, format: string): Promise<PresignedClaimFileUrlResponse> {
    const path = this.backendConfig.createPresignedDownloadUrlsPath
      .replace("{claimId}", claimId)
      .replace("{fileFormat}", format)

    const headers = new Headers();
    headers.set('Authorization', `Bearer ${this.authService.getIdToken()}`);
    const url = `${this.backendConfig.baseUrl}${path}`

    return await fetch(
      url,
      {
        method: 'GET',
        headers: headers
      }
    ).then(r => r.json()) as PresignedClaimFileUrlResponse
  }

  public async searchClaims(
    insuranceClaimReference: string,
    year: number,
    pageSize: number = 10000,
    lastEvaluatedKey: string | undefined = undefined,
    startDate: Date | undefined = undefined,
    endDate: Date | undefined = undefined
  ): Promise<ClaimListResponse> {

    const path = this.backendConfig.getSearchClaimsByInsuranceClaimReferencePath

    const parameters: Record<string, string> = {
      insurance_claim_reference: insuranceClaimReference.toString(),
      page_size: pageSize.toString(),
      year: year.toString(),
    };
    if (startDate && startDate.constructor.name === 'Date') {
      parameters['startDate'] = this.createDatePrefix(startDate)
    }
    if (endDate && endDate.constructor.name === 'Date') {
      parameters['endDate'] = this.createDatePrefix(endDate)
    }
    if (lastEvaluatedKey && lastEvaluatedKey.length > 0) {
      parameters['last_evaluated_key'] = lastEvaluatedKey
    }

    const queryParams = new URLSearchParams(parameters);
    const postFix = Object.keys(parameters).length > 0 ? `?${queryParams.toString()}` : ''

    const url = `${this.backendConfig.baseUrl}${path}${postFix}`

    const headers = new Headers();
    headers.set('Authorization', `Bearer ${this.authService.getIdToken()}`);

    return await fetch(
      url,
      {
        method: 'GET',
        headers: headers
      }
    ).then(r => r.json()) as ClaimListResponse
  }

  public async downloadClaims(
    insuranceClaimReference: string,
    startDate: Date | undefined = undefined,
    endDate: Date | undefined = undefined
  ): Promise<BatchDownloadResponse> {

    const path = this.backendConfig.getDownloadClaimsByInsuranceClaimReferencePath
    const parameters: Record<string, string> = {insurance_claim_reference: insuranceClaimReference.toString()};
    if (startDate && startDate.constructor.name === 'Date') {
      parameters['startDate'] = this.createDatePrefix(startDate)
    }
    if (endDate && endDate.constructor.name === 'Date') {
      parameters['endDate'] = this.createDatePrefix(endDate)
    }

    const queryParams = new URLSearchParams(parameters);
    const postFix = Object.keys(parameters).length > 0 ? `?${queryParams.toString()}` : ''

    const url = `${this.backendConfig.baseUrl}${path}${postFix}`

    const headers = new Headers();
    headers.set('Authorization', `Bearer ${this.authService.getIdToken()}`);

    return await fetch(
      url,
      {
        method: 'POST',
        headers: headers
      }
    )
      .then(r => {
        if (r.ok) {
          return r.json()
        } else {
          throw new Error(r.statusText)
        }
      })
      .then(r => r as BatchDownloadResponse)
      .then(r => {
        console.log("BatchDownloadResponse", r)
        return r
      })
  }

}
